/**
 * On a phone held upright the forge page has room for one column, so the
 * conversation and the forge panel (status, art, test builds, publishing)
 * take turns. This switch sits under the header and is hidden from `md` up,
 * where both columns show side by side.
 *
 * The tab that is not showing says when it wants attention: Chat when Claude
 * is waiting on the owner (or still working), Forge when screenshots are new.
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ForgeMobileView = 'chat' | 'panel';

/** Ids the switch's tabs point at (`aria-controls`). */
export const FORGE_CHAT_VIEW_ID = 'forge-chat-view';
export const FORGE_PANEL_VIEW_ID = 'forge-panel-view';

interface ForgeMobileViewSwitchProps {
  view: ForgeMobileView;
  onViewChange: (view: ForgeMobileView) => void;
  /** `waiting`: a question, plan or art batch waits for the owner. */
  chatAttention: 'waiting' | 'working' | null;
  unseenScreenshots: number;
}

export function ForgeMobileViewSwitch({
  view,
  onViewChange,
  chatAttention,
  unseenScreenshots,
}: ForgeMobileViewSwitchProps) {
  const tab = (target: ForgeMobileView, label: string, extra: ReactNode) => {
    const selected = view === target;
    return (
      <button
        type="button"
        role="tab"
        id={`${target === 'chat' ? FORGE_CHAT_VIEW_ID : FORGE_PANEL_VIEW_ID}-tab`}
        aria-selected={selected}
        aria-controls={target === 'chat' ? FORGE_CHAT_VIEW_ID : FORGE_PANEL_VIEW_ID}
        onClick={() => onViewChange(target)}
        className={cn(
          'flex h-10 items-center justify-center gap-1.5 border-b-2 text-sm transition-colors',
          selected
            ? 'border-primary font-medium text-foreground'
            : 'border-transparent text-muted-foreground hover:text-foreground',
        )}
      >
        {label}
        {!selected && extra}
      </button>
    );
  };

  return (
    <div
      role="tablist"
      aria-label="Forge view"
      data-testid="forge-mobile-view-switch"
      className="grid grid-cols-2 border-b md:hidden"
    >
      {tab(
        'chat',
        'Chat',
        chatAttention === 'waiting' ? (
          <span data-testid="forge-chat-needs-you">
            <span className="block h-2 w-2 rounded-full bg-primary" aria-hidden />
            <span className="sr-only">(waiting for you)</span>
          </span>
        ) : chatAttention === 'working' ? (
          <span data-testid="forge-chat-working">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            <span className="sr-only">(Claude Code is working)</span>
          </span>
        ) : null,
      )}
      {tab(
        'panel',
        'Forge',
        unseenScreenshots > 0 ? (
          <span data-testid="forge-panel-unseen">
            <span
              className="block rounded-full bg-primary px-1.5 text-[10px] font-medium leading-4 text-primary-foreground"
              aria-hidden
            >
              {unseenScreenshots}
            </span>
            <span className="sr-only">
              ({unseenScreenshots} new screenshot{unseenScreenshots === 1 ? '' : 's'})
            </span>
          </span>
        ) : null,
      )}
    </div>
  );
}
